import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import api from '../lib/api';
import { itemsForRole, ALL_ROLES } from '../lib/menuItems';

import { isAdminLevel, isSuperAdmin } from '../lib/utils';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full system access', superOnly: true },
  { value: 'manager', label: 'Manager', desc: 'Management operations' },
  { value: 'salesman', label: 'Salesman', desc: 'Sales operations' },
  { value: 'inventory_staff', label: 'Inventory Staff', desc: 'Inventory management' },
  { value: 'accountant', label: 'Accountant', desc: 'Accounting & finance' },
  { value: 'colourman', label: 'Colourman', desc: 'Color management' },
  { value: 'packing_department', label: 'Packing Dept', desc: 'Packing operations' },
  { value: 'agent', label: 'Agent', desc: 'Agent operations' },
  { value: 'distributor', label: 'Distributor', desc: 'Distribution' },
];

const DAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
];

const pad2 = (n) => String(n).padStart(2, '0');

// "HH:MM" (24h) -> { h12, m, ampm }
const parse12 = (val) => {
  const [hStr, mStr] = (val || '10:00').split(':');
  let h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { h12, m, ampm };
};

// 12h parts -> "HH:MM" (24h) for storage
const to24 = (h12, m, ampm) => {
  let h = h12 % 12;
  if (ampm === 'PM') h += 12;
  return `${pad2(h)}:${pad2(m)}`;
};

const SELECT_CLS = 'h-8 rounded-md border border-input bg-background px-1.5 text-sm';

const TimeSelect = ({ value, onChange, testid }) => {
  const { h12, m, ampm } = parse12(value);
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  if (!minutes.includes(m)) minutes.push(m);
  minutes.sort((a, b) => a - b);
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <select value={h12} onChange={e => onChange(to24(parseInt(e.target.value, 10), m, ampm))} className={SELECT_CLS} aria-label="Hour">
        {hours.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-gray-400">:</span>
      <select value={m} onChange={e => onChange(to24(h12, parseInt(e.target.value, 10), ampm))} className={SELECT_CLS} aria-label="Minute">
        {minutes.map(mm => <option key={mm} value={mm}>{pad2(mm)}</option>)}
      </select>
      <select value={ampm} onChange={e => onChange(to24(h12, m, e.target.value))} className={SELECT_CLS} aria-label="AM/PM">
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const ROLE_COLORS = {
  super_admin: 'bg-violet-100 text-violet-800',
  admin: 'bg-red-100 text-red-800',
  manager: 'bg-purple-100 text-purple-800',
  salesman: 'bg-blue-100 text-blue-800',
  inventory_staff: 'bg-green-100 text-green-800',
  accountant: 'bg-teal-100 text-teal-800',
  colourman: 'bg-amber-100 text-amber-800',
  packing_department: 'bg-cyan-100 text-cyan-800',
  agent: 'bg-indigo-100 text-indigo-800',
  distributor: 'bg-orange-100 text-orange-800',
};

const UserManagement = ({ user: currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', username: '', role: '', password: '', requires_approval_so: false, requires_approval_po: false });
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [accessEnabled, setAccessEnabled] = useState(true);
  const [restrictHours, setRestrictHours] = useState(false);
  const [schedule, setSchedule] = useState({});

  const [createForm, setCreateForm] = useState({
    email: '', username: '', full_name: '', password: '', role: 'salesman', watermark_code: '',
  });

  const isAdmin = isAdminLevel(currentUser?.role);
  const isSuperAdminUser = isSuperAdmin(currentUser?.role);

  // Filter roles: only super_admin can create admin users
  const availableRoles = isSuperAdminUser ? ROLES : ROLES.filter(r => !r.superOnly);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/register', createForm);
      toast.success(`User "${createForm.full_name}" created!`);
      setIsCreateOpen(false);
      setCreateForm({ email: '', username: '', full_name: '', password: '', role: 'salesman', watermark_code: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.put(`/users/${userId}`, updates);
      toast.success('User updated!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`${name} deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const togglePermission = async (userId, permKey, currentValue) => {
    const user = users.find(u => u.id === userId);
    const newPerms = { ...(user?.permissions || {}), [permKey]: !currentValue };
    await handleUpdateUser(userId, { permissions: newPerms });
  };

  const toggleActive = async (userId, currentActive) => {
    await handleUpdateUser(userId, { is_active: !currentActive });
  };

  const openAccessControl = (u) => {
    setAccessUser(u);
    setAccessEnabled(u.access_enabled !== false);
    const sched = u.access_schedule;
    setRestrictHours(!!sched);
    const init = {};
    DAYS.forEach(([k]) => {
      const d = sched?.[k];
      init[k] = { enabled: d?.enabled ?? false, start: d?.start || '10:00', end: d?.end || '21:00' };
    });
    setSchedule(init);
  };

  const updateScheduleDay = (key, field, value) => {
    setSchedule(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const saveAccessControl = async () => {
    if (!accessUser) return;
    if (restrictHours) {
      for (const [k, label] of DAYS) {
        const d = schedule[k];
        if (d?.enabled) {
          if (!d.start || !d.end) { toast.error(`Set start & end time for ${label}`); return; }
          if (d.start >= d.end) { toast.error(`${label}: start time must be before end time`); return; }
        }
      }
    }
    try {
      await api.put(`/users/${accessUser.id}/access`, {
        access_enabled: accessEnabled,
        access_schedule: restrictHours ? schedule : null,
      });
      toast.success(`Access settings updated for ${accessUser.full_name}`);
      setAccessUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access settings');
    }
  };

  const MODULE_PERMISSIONS = [
    { path: '/brands', label: 'Brands' },
    { path: '/vendors', label: 'Vendors' },
    { path: '/warehouses', label: 'Warehouses' },
    { path: '/products', label: 'Products' },
    { path: '/purchase-orders', label: 'Purchase Orders' },
    { path: '/purchase-entry', label: 'Purchase Entry' },
    { path: '/pe-dashboard', label: 'PE Dashboard' },
    { path: '/purchase-returns', label: 'Purchase Returns' },
    { path: '/po-dashboard', label: 'PO Dashboard' },
    { path: '/vendor-analytics', label: 'Vendor Analytics' },
    { path: '/agents', label: 'Agents' },
    { path: '/distributors', label: 'Distributors' },
    { path: '/distributor-dispatch', label: 'Distributor Dispatch' },
    { path: '/customers', label: 'Customers' },
    { path: '/sales-orders', label: 'Sales Orders' },
    { path: '/sales-entry', label: 'Sales Entry' },
    { path: '/sales-returns', label: 'Sales Returns' },
    { path: '/so-dashboard', label: 'Sales Dashboard' },
    { path: '/geo-dashboard', label: 'India Map' },
    { path: '/operations', label: 'Operations Hub' },
    { path: '/reports', label: 'Reports' },
  ];

  const ACTION_PERMISSIONS = [
    { key: 'can_edit', label: 'Can Edit Records' },
    { key: 'can_delete', label: 'Can Delete Records' },
    { key: 'can_view_vendor', label: 'Can View Vendor Details (Catalogue)' },
    { key: 'can_export', label: 'Can Export Reports' },
    { key: 'can_create_po', label: 'Can Create Purchase Orders' },
    { key: 'can_access_vendors', label: 'Can Access Vendors Page' },
    { key: 'can_access_customers', label: 'Can Access Customers Page' },
    { key: 'can_access_agents', label: 'Can Access Agents Page' },
    { key: 'can_access_distributors', label: 'Can Access Distributors Page' },
    { key: 'can_access_daily_summary', label: 'Can Access Daily Summary' },
  ];

  const toggleModuleHidden = async (userId, modulePath) => {
    const user = users.find(u => u.id === userId);
    const perms = { ...(user?.permissions || {}) };
    const hidden = [...(perms.hidden_modules || [])];
    const idx = hidden.indexOf(modulePath);
    if (idx >= 0) hidden.splice(idx, 1);
    else hidden.push(modulePath);
    perms.hidden_modules = hidden;
    try {
      await api.put(`/users/${userId}`, { permissions: perms });
      toast.success('Permission updated');
      const res = await api.get('/users');
      setUsers(res.data);
      const updated = res.data.find(u => u.id === userId);
      if (updated) setPermissionsUser(updated);
    } catch (err) { toast.error('Failed to update permission'); }
  };

  const toggleActionPerm = async (userId, key) => {
    const user = users.find(u => u.id === userId);
    const perms = { ...(user?.permissions || {}), [key]: !user?.permissions?.[key] };
    try {
      await api.put(`/users/${userId}`, { permissions: perms });
      toast.success('Permission updated');
      const res = await api.get('/users');
      setUsers(res.data);
      const updated = res.data.find(u => u.id === userId);
      if (updated) setPermissionsUser(updated);
    } catch (err) { toast.error('Failed to update permission'); }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    const ra = u.requires_approval || {};
    setEditForm({ full_name: u.full_name || '', email: u.email || '', username: u.username || '', role: u.role || 'salesman', password: u.plain_password || '', watermark_code: u.watermark_code || '', requires_approval_so: !!ra.so, requires_approval_po: !!ra.po });
    setShowEditPassword(false);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    const updates = {};
    if (editForm.full_name && editForm.full_name !== editingUser.full_name) updates.full_name = editForm.full_name;
    if (editForm.email && editForm.email !== editingUser.email) updates.email = editForm.email;
    if (editForm.username && editForm.username !== editingUser.username) updates.username = editForm.username;
    if (editForm.role && editForm.role !== editingUser.role) updates.role = editForm.role;
    if (editForm.watermark_code !== (editingUser.watermark_code || '')) updates.watermark_code = editForm.watermark_code;
    // Approval toggles
    const oldRA = editingUser.requires_approval || {};
    const newRA = { so: editForm.requires_approval_so, po: editForm.requires_approval_po };
    if (newRA.so !== !!oldRA.so || newRA.po !== !!oldRA.po) {
      updates.requires_approval = newRA;
    }
    // Handle password change
    const passwordChanged = editForm.password && editForm.password !== (editingUser.plain_password || '');
    if (passwordChanged) {
      if (editForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
      try {
        await api.post(`/users/${editingUser.id}/reset-password`, { new_password: editForm.password });
        toast.success('Password updated');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update password'); return; }
    }
    if (Object.keys(updates).length === 0 && !passwordChanged) { toast.info('No changes made'); setEditingUser(null); return; }
    if (Object.keys(updates).length > 0) {
      await handleUpdateUser(editingUser.id, updates);
    } else {
      setEditingUser(null);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/users/${resetPasswordUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetPasswordUser.full_name}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="user-management-container">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>User Management</h1>
          <p className="text-gray-600 mt-1">Manage users, roles, and permissions</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RolePreviewDialog />
          {isAdmin && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg" data-testid="add-user-button">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg z-50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>Add a new team member to Garment360</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input value={createForm.full_name} onChange={e => setCreateForm(p => ({ ...p, full_name: e.target.value }))} required data-testid="create-user-name" placeholder="e.g., Rahul Sharma" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} required data-testid="create-user-email" placeholder="rahul@company.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <Input value={createForm.username} onChange={e => setCreateForm(p => ({ ...p, username: e.target.value }))} required data-testid="create-user-username" placeholder="rahul_s" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <div className="relative">
                      <Input type={showCreatePassword ? 'text' : 'password'} value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} required data-testid="create-user-password" placeholder="Min 6 characters" className="pr-10" />
                      <button type="button" onClick={() => setShowCreatePassword(!showCreatePassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-create-password">
                        {showCreatePassword ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <select value={createForm.role} onChange={e => setCreateForm(p => ({ ...p, role: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="create-user-role">
                      {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Watermark Code</Label>
                  <Input value={createForm.watermark_code} onChange={e => setCreateForm(p => ({ ...p, watermark_code: e.target.value.toUpperCase() }))} data-testid="create-user-watermark" placeholder="e.g., WM001" className="font-mono" />
                  <p className="text-[10px] text-gray-400">Unique code displayed as watermark when this user is logged in. Helps trace data leaks.</p>
                </div>
                <div className="flex gap-4 pt-6">
                  <Button type="submit" className="flex-1" data-testid="submit-create-user">Create User</Button>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Users List */}
      <Card data-testid="users-list">
        <CardHeader>
          <CardTitle>Team Members ({users.length})</CardTitle>
          <CardDescription>All registered users and their permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">User</th>
                  <th className="text-left p-3 font-medium text-gray-600">Role</th>
                  <th className="text-left p-3 font-medium text-gray-600">Code</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-center p-3 font-medium text-gray-600">Can View Vendor</th>
                  <th className="text-left p-3 font-medium text-gray-600">Last Login</th>
                  {isAdmin && <th className="text-right p-3 font-medium text-gray-600">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isSelf = u.id === currentUser?.id;
                  const canViewVendor = u.role === 'admin' || u.permissions?.can_view_vendor;
                  const showVendorToggle = !isSelf;
                  return (
                    <tr key={u.id} className="border-b hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name} {isSelf && <span className="text-xs text-blue-600">(you)</span>}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        {isAdmin && !isSelf ? (
                          <select value={u.role} onChange={e => handleUpdateUser(u.id, { role: e.target.value })}
                            className="text-xs rounded-md border border-input bg-background px-2 py-1" data-testid={`role-select-${u.id}`}>
                            {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        ) : (
                          <Badge className={`text-xs capitalize ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-800'}`}>
                            {u.role?.replace('_', ' ')}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="font-mono text-xs text-gray-500">{u.watermark_code || '—'}</span>
                      </td>
                      <td className="p-3">
                        {isAdmin && !isSelf ? (
                          <button onClick={() => toggleActive(u.id, u.is_active)}
                            className={`text-xs px-2 py-1 rounded-full font-medium ${u.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                            data-testid={`toggle-active-${u.id}`}>
                            {u.is_active ? 'Active' : 'Suspended'}
                          </button>
                        ) : (
                          <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {u.is_active ? 'Active' : 'Suspended'}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {showVendorToggle && isAdmin ? (
                          <button onClick={() => togglePermission(u.id, 'can_view_vendor', canViewVendor)}
                            className={`relative inline-flex h-5 w-10 rounded-full transition-colors ${canViewVendor ? 'bg-blue-600' : 'bg-gray-300'}`}
                            data-testid={`vendor-toggle-${u.id}`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${canViewVendor ? 'translate-x-5' : 'translate-x-1'}`} />
                          </button>
                        ) : (
                          <span className={`text-xs font-medium ${canViewVendor ? 'text-green-600' : 'text-gray-400'}`}>
                            {canViewVendor ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {u.last_login ? (() => { const d = new Date(u.last_login); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getFullYear()).slice(-2)}`; })() : 'Never'}
                      </td>
                      {isAdmin && (
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            {isSuperAdminUser && !isSelf && u.role !== 'super_admin' && (
                              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => openAccessControl(u)} data-testid={`access-control-${u.id}`} title="Access Control (on/off + login hours)">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              </Button>
                            )}
                            {!isSelf && (
                              <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50" onClick={() => setPermissionsUser(u)} data-testid={`permissions-user-${u.id}`} title="Manage Permissions">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`} title="Edit User">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => { setResetPasswordUser(u); setNewPassword(''); }} data-testid={`reset-password-${u.id}`} title="Reset Password">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                            </Button>
                            {!isSelf && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete User</AlertDialogTitle>
                                    <AlertDialogDescription>Are you sure you want to delete "{u.full_name}"? This cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteUser(u.id, u.full_name)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-user-${u.id}`}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={v => { if (!v) { setResetPasswordUser(null); setNewPassword(''); } }}>
        <DialogContent className="max-w-sm z-50">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPasswordUser?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <div className="relative">
                <Input type={showResetPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="reset-password-input" autoFocus className="pr-10" />
                <button type="button" onClick={() => setShowResetPassword(!showResetPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-reset-password">
                  {showResetPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={newPassword.length < 6} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-password-submit">
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={v => { if (!v) setEditingUser(null); }}>
        <DialogContent className="max-w-lg z-50">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update details for {editingUser?.full_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={editForm.full_name} onChange={e => setEditForm(p => ({ ...p, full_name: e.target.value }))} required data-testid="edit-user-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} required data-testid="edit-user-email" />
              </div>
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input value={editForm.username} onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))} required data-testid="edit-user-username" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-user-role">
                {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input type={showEditPassword ? 'text' : 'password'} value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Password" data-testid="edit-user-password" />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowEditPassword(!showEditPassword)}>
                  {showEditPassword ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Watermark Code</Label>
              <Input value={editForm.watermark_code} onChange={e => setEditForm(p => ({ ...p, watermark_code: e.target.value.toUpperCase() }))} data-testid="edit-user-watermark" placeholder="e.g., WM001" className="font-mono" />
              <p className="text-[10px] text-gray-400">Unique code displayed as watermark. Helps trace data leaks.</p>
            </div>
            {/* Approval Toggles */}
            {editForm.role !== 'super_admin' && editForm.role !== 'admin' && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Requires Approval</Label>
                <p className="text-[10px] text-gray-400 -mt-1">When enabled, this user's SO/PO will need admin approval before processing.</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 hover:bg-gray-50">
                    <input type="checkbox" checked={editForm.requires_approval_so}
                      onChange={e => setEditForm(p => ({ ...p, requires_approval_so: e.target.checked }))}
                      className="accent-blue-600 w-4 h-4" data-testid="edit-user-approval-so" />
                    <span className="text-sm">Sales Order (SO)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 hover:bg-gray-50">
                    <input type="checkbox" checked={editForm.requires_approval_po}
                      onChange={e => setEditForm(p => ({ ...p, requires_approval_po: e.target.checked }))}
                      className="accent-blue-600 w-4 h-4" data-testid="edit-user-approval-po" />
                    <span className="text-sm">Purchase Order (PO)</span>
                  </label>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="edit-user-submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={!!permissionsUser} onOpenChange={v => { if (!v) setPermissionsUser(null); }}>
        <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Permissions — {permissionsUser?.full_name}</DialogTitle>
            <DialogDescription>Role: {permissionsUser?.role?.replace('_', ' ')} | Toggle modules and actions this user can access</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 pt-2">
            {/* Action Permissions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Action Permissions</h3>
              <div className="grid grid-cols-2 gap-3">
                {ACTION_PERMISSIONS.map(perm => {
                  const enabled = permissionsUser?.permissions?.[perm.key] ?? (permissionsUser?.role === 'admin' || permissionsUser?.role === 'manager');
                  return (
                    <div key={perm.key} className="flex items-center justify-between rounded-lg border p-3">
                      <span className="text-sm">{perm.label}</span>
                      <button onClick={() => toggleActionPerm(permissionsUser.id, perm.key)}
                        className={`relative inline-flex h-5 w-10 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                        data-testid={`perm-action-${perm.key}-${permissionsUser?.id}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Module Access */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Module Access (toggle to hide/show)</h3>
              <div className="grid grid-cols-2 gap-2">
                {MODULE_PERMISSIONS.map(mod => {
                  const isHidden = (permissionsUser?.permissions?.hidden_modules || []).includes(mod.path);
                  return (
                    <div key={mod.path} className={`flex items-center justify-between rounded-lg border p-2.5 ${isHidden ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <span className="text-sm">{mod.label}</span>
                      <button onClick={() => toggleModuleHidden(permissionsUser.id, mod.path)}
                        className={`relative inline-flex h-5 w-10 rounded-full transition-colors ${!isHidden ? 'bg-green-600' : 'bg-red-400'}`}
                        data-testid={`perm-module-${mod.path.replace(/\//g, '')}-${permissionsUser?.id}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${!isHidden ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setPermissionsUser(null)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access Control Dialog (Super Admin only) */}
      <Dialog open={!!accessUser} onOpenChange={v => { if (!v) setAccessUser(null); }}>
        <DialogContent className="max-w-lg z-50 max-h-[90vh] overflow-y-auto" data-testid="access-control-dialog">
          <DialogHeader>
            <DialogTitle>Access Control — {accessUser?.full_name}</DialogTitle>
            <DialogDescription>Turn access on/off and restrict login to specific hours (India Standard Time).</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Master on/off */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">Access Enabled</p>
                <p className="text-xs text-gray-500">If turned off, this user cannot log in or use the system at all.</p>
              </div>
              <Switch checked={accessEnabled} onCheckedChange={setAccessEnabled} data-testid="access-enabled-toggle" />
            </div>

            {/* Restrict hours */}
            <div className={`rounded-lg border p-4 ${accessEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Restrict Login Hours (IST)</p>
                  <p className="text-xs text-gray-500">When on, the user can only access during the configured hours per day.</p>
                </div>
                <Switch checked={restrictHours} onCheckedChange={setRestrictHours} data-testid="restrict-hours-toggle" />
              </div>

              {restrictHours && (
                <div className="mt-4 space-y-2" data-testid="schedule-editor">
                  {DAYS.map(([key, label]) => {
                    const d = schedule[key] || { enabled: false, start: '10:00', end: '21:00' };
                    return (
                      <div key={key} className="flex items-center gap-3 flex-wrap">
                        <label className="flex items-center gap-2 w-32 shrink-0 cursor-pointer">
                          <input type="checkbox" checked={d.enabled} onChange={e => updateScheduleDay(key, 'enabled', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" data-testid={`day-enabled-${key}`} />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                        {d.enabled ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <TimeSelect value={d.start} onChange={v => updateScheduleDay(key, 'start', v)} testid={`day-start-${key}`} />
                            <span className="text-xs text-gray-400">to</span>
                            <TimeSelect value={d.end} onChange={v => updateScheduleDay(key, 'end', v)} testid={`day-end-${key}`} />
                            <span className="text-[11px] text-gray-400 font-medium">IST</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No access this day</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!restrictHours && (
                <p className="mt-2 text-xs text-gray-400">No time restriction — access allowed 24/7 (subject to the toggle above).</p>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setAccessUser(null)} data-testid="access-control-cancel">Cancel</Button>
              <Button onClick={saveAccessControl} className="bg-amber-600 hover:bg-amber-700" data-testid="access-control-save">Save Access Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;

// ─────────── Role Preview Dialog ───────────
function RolePreviewDialog() {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState('manager');
  const items = itemsForRole(role);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="role-preview-trigger" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          Preview Role Access
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl z-50 max-h-[85vh] overflow-y-auto" data-testid="role-preview-dialog">
        <DialogHeader>
          <DialogTitle>Role Access Preview</DialogTitle>
          <DialogDescription>
            See exactly which sidebar menu items a role will be able to access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm">Role:</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_ROLES.map(r => (
                <button
                  key={r.value}
                  type="button"
                  data-testid={`role-preview-role-${r.value}`}
                  onClick={() => setRole(r.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${role === r.value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t pt-4">
            <p className="text-sm text-gray-700 mb-3">
              <span className="font-semibold">{ALL_ROLES.find(r => r.value === role)?.label}</span> will see <span className="font-semibold">{items.length}</span> menu item{items.length !== 1 ? 's' : ''}:
            </p>
            {items.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="role-preview-empty">No menu items accessible by this role.</p>
            ) : (
              <ol className="space-y-1.5 text-sm bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="role-preview-list">
                {items.map((it, idx) => (
                  <li key={it.path} className="flex items-center gap-3 px-2 py-1.5 hover:bg-white rounded">
                    <span className="text-xs text-gray-400 w-6 text-right">{idx + 1}.</span>
                    <span className="text-base">{it.icon}</span>
                    <span className="font-medium text-slate-800">{it.name}</span>
                    <code className="ml-auto text-[10px] text-gray-400 font-mono">{it.path}</code>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Note: This preview reflects role-based access only. Per-user <code>permKey</code> grants and <code>allowedUserIds</code> overrides can add extra items per individual user.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
